import {
  RestAuthenticatedClient,
  RestPublicClient,
} from '@katanaperps/katana-perps-sdk';

import config from 'config';
import {
  APP_VERSION,
  ETH_ADDRESS_ZERO,
  IGNORED_NONCE,
  PayoutAsDepositProgramCode,
  PayoutProgramCode,
} from 'config/constants';
import { getActiveChainFromState } from 'reduxState/exchange/exchangeActiveChain';
import { Chains } from 'reduxState/exchange/exchangeActiveChainUtils';

import {
  ApiTradingCompetitionSandbox,
  UpdateCompetitionParticipantParamsSandbox,
} from './apiCompetitionSandbox';
import {
  ApiTradingCompetitionResponse,
  ApiTradingCompetitionsResponse,
  UpdateCompetitionParticipantParams,
} from './apiCompetitionTypes';
import { patchClientToIdexInternal } from './apiPatch';
import {
  APIBankrollPointsRedeemResponse,
  APIBankrollPointsResponse,
  APIBankrollRedeemResponse,
  APIBankrollResponse,
} from './bankrollTypes';

import type {
  APIUser,
  ApiNotices,
  ApiUserTokens,
  ApiUserBearerToken,
  ApiUserNewToken,
  ApiTicker,
  ApiInternalCandles,
  StakingChains,
  ApiStakingTier3,
  ApiStakingTier2,
  ApiReferralsStatus,
  // APITradingRewardsPeriod,
  // APITradingRewardsPeriodStatus,
  // APITradingRewardHistoryPaginated,
  ApiStakingHostedNode,
  // OnrampConfigResponse,
  // OnrampsValidGetServices,
  // OnrampServiceConfigResponse,
  ApiDelegatedKeys,
  ApiDelegatedKey,
  ApiPayoutAuthorization,
  ApiBalanceHistoryRaw,
  ApiInitialMarginFractionOverride,
  ApiPayoutProgram,
  ApiMarketMakerWalletRewards,
} from './types';
import type {
  DelegatedKeyCreationParamsOptional,
  KatanaPerpsVault,
  Paginated,
  RestRequestFarmPayout,
  RestRequestGetCandles,
  RestRequestGetFundingPayments,
  RestRequestGetHistoricalPnL,
  RestRequestGetInitialMarginFractionOverride,
  RestRequestGetKatanaPoints,
  RestRequestGetVaultBalanceHistory,
  RestRequestGetVaultDepositorBalanceHistory,
  RestRequestGetVaultDepositors,
  RestRequestGetVaults,
  RestRequestGetWallets,
  RestResponseAssociateWallet,
  RestResponseGetFill,
  RestResponseGetFundingPayments,
  RestResponseGetHistoricalPnL,
  RestResponseGetInitialMarginFractionOverride,
  RestResponseGetKatanaPoints,
  RestResponseGetVaultBalanceHistory,
  RestResponseGetVaultDepositorBalanceHistory,
  RestResponseGetVaultDepositors,
  RestResponseGetVaults,
  RestResponseGetWallets,
} from '@katanaperps/katana-perps-sdk';

const axiosConfig = {
  withCredentials: true,
  headers: { 'KP-Web-Client': APP_VERSION },
};

class RestPublicInternalClient extends RestPublicClient {
  public async getInternalPublicWalletSettings(walletAddress: string): Promise<{
    /**
     * All walllets should have this set to true once implemented fully
     *
     * @see [Spec](https://docs.google.com/document/d/19ebLbiSR2YKXuZeqCknRLDUT1VHORMiPas_Q-ZOjAuU/edit#heading=h.tfmkhg8ydxyq)
     */
    tosAccepted?: boolean;
  }> {
    return this.get('/internal/walletSettings', {
      wallet: walletAddress,
    });
  }

  public async getPublicReferralCodeValidity(
    referralCode: string
  ): Promise<{ isValid: boolean }> {
    return this.get('/internal/referrals', { code: referralCode });
  }
}

/**
 * Extended version of the SDK which provides internal calls made using the same
 * library and parameters as the sdk for consistency.
 */
class RestAuthenticatedInternalClient extends RestAuthenticatedClient {
  constructor({
    baseURL,
    chainId,
    exchangeContractAddress,
    bridgeAdapterContractAddress,
    sandbox,
  }: {
    baseURL: string;
    sandbox: boolean;
    chainId: number;
    exchangeContractAddress: string;
    bridgeAdapterContractAddress: string;
  }) {
    super({
      baseURL,
      // do not include the api key header for web client
      apiKey: '',
      apiSecret: '',
      axiosConfig,
      sandbox,
      chainId,
      exchangeContractAddress,
      bridgeAdapterContractAddress,
      autoCreateHmacHeader: false,
    });
  }

  /**
   * Get tickers call with internal web-client specific parameter for filtering by `quoteAsset`.
   *
   * @see https://docs.google.com/document/d/1uPaR0jqisaDor7-dyUqW2VeakwZanJwgqXRXiNQwF94/edit#heading=h.humy3n2lozef
   * @internal
   */
  public async getTickersByQuote(quoteAsset: string): Promise<ApiTicker[]> {
    return this.get('/tickers', { quoteAsset });
  }

  /**
   * Get public tickers but using user session (which can be specific to user region)
   * @internal
   */
  public async getTickers(market: string): Promise<ApiTicker[]> {
    return this.get('/tickers', { market });
  }

  /*
   * The special internal method will return URL which will return  downloadable trades history CSV.
   *
   * @see https://docs.google.com/document/d/1uPaR0jqisaDor7-dyUqW2VeakwZanJwgqXRXiNQwF94/edit#heading=h.7ywfodu8ouc2
   * @internal
   */
  public getInternalFillHistoryCsvLink({ wallet }: { wallet: string }) {
    return `${katanaChain.restApiUrl}/internal/csv/fillHistory?wallet=${wallet}`;
  }

  public getInternalFundingHistoryCsvLink({ wallet }: { wallet: string }) {
    return `${katanaChain.restApiUrl}/internal/csv/fundingPayments?wallet=${wallet}`;
  }

  public getInternalBalanceHistoryCsvLink({ wallet }: { wallet: string }) {
    return `${katanaChain.restApiUrl}/internal/csv/balanceHistory?wallet=${wallet}`;
  }

  public getInternalStakingEarningsCsvLink({ wallet }: { wallet: string }) {
    // TODO: API rename stakingEarnings ?
    return `${katanaChain.restApiUrl}/internal/csv/staking?wallet=${wallet}`;
  }

  /*
   * The internal call to get trades history CSV downloadable results.
   *
   * @see https://docs.google.com/document/d/1uPaR0jqisaDor7-dyUqW2VeakwZanJwgqXRXiNQwF94/edit#heading=h.7ywfodu8ouc2
   * @internal
   */
  // public async getInternalFillHistoryCSV({
  //   wallet,
  // }: {
  //   wallet: string;
  // }): Promise<string> {
  //   return this.getWithHeaders('/internal/fillHistoryCSV', { wallet });
  // }

  /**
   * The internal call to get the web-client specific result for `get fills` which is slightly different
   * from the public endpoint.
   *
   * @see https://docs.google.com/document/d/1uPaR0jqisaDor7-dyUqW2VeakwZanJwgqXRXiNQwF94/edit#heading=h.rkpj7ak9jbhc
   * @internal
   */
  public async getInternalFills({
    wallet,
    isVault,
    page,
    limit,
    market,
  }: {
    wallet: string;
    isVault: boolean;
    market?: string;
    page: number;
    limit: number;
  }): Promise<Paginated<RestResponseGetFill[]>> {
    return this.get(
      isVault ? '/internal/vaults/fills' : '/internal/fills',
      market
        ? {
            wallet,
            page,
            limit,
            // TODO: API: array not needed anymore - single market OK
            markets: market, // params.markets.join(','),
          }
        : { wallet, page, limit }
    );
  }

  public async getInternalMarketMakertRewards({
    wallet,
  }: {
    wallet: string;
  }): Promise<ApiMarketMakerWalletRewards> {
    return this.get('/internal/pointsProgram/makerRewards', { wallet });
  }

  // /**
  //  * @internal
  //  */
  // public async getInternalTradingRewardsPeriods(): Promise<
  //   APITradingRewardsPeriod[]
  // > {
  //   return this.get('/internal/tradingRewardsV2/periods');
  // }

  // /**
  //  * @internal
  //  */
  // public async getCurrentTradingRewardsPeriod(): Promise<APITradingRewardsPeriod> {
  //   return this.get('/internal/tradingRewardsV2/period/current');
  // }

  // /**
  //  * @internal
  //  */
  // public async getInternalTradingRewardsCurrentPeriodStatus(
  //   wallet: string
  // ): Promise<APITradingRewardsPeriodStatus | null> {
  //   return this.get('/internal/tradingRewardsV2/status', { wallet });
  // }

  // public async getInternalTradingRewardsHistoryPaginated(
  //   wallet: string,
  //   page: number,
  //   limit: number
  // ): Promise<APITradingRewardHistoryPaginated> {
  //   return this.get('/internal/tradingRewardsV2/history', {
  //     wallet,
  //     page,
  //     limit,
  //   });
  // }

  public async getInternalPoints(
    wallet: string | undefined
  ): Promise<RestResponseGetKatanaPoints> {
    const params: RestRequestGetKatanaPoints = {
      wallet,
      nonce: IGNORED_NONCE,
    };
    return this.get('/internal/pointsProgram/status', params);
  }

  public async getBankroll(code: string): Promise<APIBankrollResponse> {
    return this.get('/internal/bankrolls', { code });
  }

  public async redeemBankroll({
    code,
    wallet,
  }: {
    code: string;
    wallet: string;
  }): Promise<APIBankrollRedeemResponse> {
    return this.post('/internal/bankrolls', { code, wallet });
  }

  /** Dev: dev test code is WELCOME1 */
  public async getWelcomeBonus(
    code: string,
    walletAddress?: string
  ): Promise<APIBankrollPointsResponse> {
    return this.get<APIBankrollPointsResponse>(
      '/internal/welcomeBonus',
      walletAddress ? { wallet: walletAddress, code } : { code }
    ).catch(error => {
      console.warn(error);
      // Fallback when API is returning 404 (Can be removed once API fixed)
      return { welcomeBonus: null };
    });
  }

  public async redeemWelcomeBonus({
    code,
    wallet,
  }: {
    code: string;
    wallet: string;
  }): Promise<APIBankrollPointsRedeemResponse> {
    return this.post('/internal/welcomeBonus', { code, wallet });
  }

  /** @internal */
  public async authRegister(
    body: {
      email: string;
      password: string;
      tosAccepted: true;
    },
    captchaResponse: string
  ): Promise<APIUser> {
    return this.request('/auth/register', {
      method: 'POST',
      data: body,
      headers: { 'KP-Challenge-Token': captchaResponse },
    }) as Promise<any>;
  }

  /** @internal */
  public async authLogin(
    email: string,
    password: string,
    captchaResponse: string
  ): Promise<void> {
    return this.request('/auth/login', {
      method: 'POST',
      data: { email, password },
      headers: { 'KP-Challenge-Token': captchaResponse },
    }) as Promise<any>;
  }

  /** @internal */
  public async authLogout(): Promise<void> {
    return this.post('/auth/logout');
  }

  public async getWalletGuard(
    wallet: string
  ): Promise<{ personalWalletSecret: string }> {
    return this.get('/internal/walletGuard', {
      wallet,
    });
  }

  public async getDelegatedKeys(wallet: string): Promise<ApiDelegatedKeys> {
    // requires user logged in so no point in using the header here
    return this.get('/internal/delegatedKeys', {
      wallet,
    });
  }

  /**
   * Gets only the requested delegated key, allowing querying for open orders on the key
   */
  public async getDelegatedKey(
    wallet: string,
    delegatedKey: string
  ): Promise<ApiDelegatedKey> {
    // requires user logged in so no point in using the header here
    return this.get(`/internal/delegatedKeys/${delegatedKey}`, {
      wallet,
    });
  }

  public async revokeDelegatedKey({
    wallet,
    nonce,
    delegatedKey,
    signature,
    neverRevokeWithOpenOrders,
  }: {
    wallet: string;
    delegatedKey: string;
    nonce: string;
    signature: string;
    /**
     * Will throw an error if there are open orders for the delegated key
     * defaults to false
     */
    neverRevokeWithOpenOrders?: boolean;
  }): Promise<void> {
    await this.delete<unknown>(`/internal/delegatedKeys/${delegatedKey}`, {
      parameters: {
        wallet,
        nonce,
        neverRevokeWithOpenOrders,
      },
      signature,
    });
    return;
  }

  /**
   * This request is unfinished and needs to be implemented in the API
   * @internal
   */
  public async userChangePassword(oldPassword: string, newPassword: string) {
    return this.post('/user/change-password', { oldPassword, newPassword });
  }

  /** @internal */
  public async userChangeEmail(body: {
    currentEmail: string;
    password: string;
    email: string;
  }): Promise<{ email: string }> {
    return this.put('/user/email', body);
  }

  /**
   * This is the legacy user info endpoint brought over from IDEX v1, not to be confused
   * with the `/user` endpoint via `getUser` which returns a different response type.
   *
   * @internal
   */
  public async getUserInfo(): Promise<APIUser> {
    return this.get('/user/internal/info');
  }

  /**
   * Tell server to invalidate cookie
   * @internal
   */
  public async removeWalletSessionInternal(): Promise<
    Record<string, undefined>
  > {
    const { data } = await this.request('/internal/walletSession', {
      method: 'DELETE',
      data: {},
    });
    return data;
  }

  /**
   * The internal wallet association method, not to be confused with the `associateWallet`
   * method which is the public method and expected a different call signature.
   * /internal/walletSession
   * @internal
   */
  public async internalAssociateWallet({
    walletAddress,
    walletType,
    nonce,
    signature,
    delegatedKey,
    delegatedName,
    referralCode,
    welcomeBonusCode,
  }: {
    walletAddress: string;
    walletType: string;
    signature: string;
    nonce: string;
    referralCode?: string;
    welcomeBonusCode?: string;
  } & Partial<DelegatedKeyCreationParamsOptional>): Promise<
    RestResponseAssociateWallet & {
      didRedeemReferralCode?: boolean;
      welcomeBonusSuccessMessage: string | null;
    }
  > {
    const { data } = await this.request('/internal/walletsAssociate', {
      method: 'POST',
      data: {
        tosAccepted: true,
        // Apply one of available codes. Second (if set) will be applied after unlock.
        referralOrWelcomeBonusCode:
          referralCode || welcomeBonusCode || undefined,
        parameters: {
          wallet: walletAddress,
          nonce,
          walletType,
          // associates delegatdKey and expects sig had nonce.
          ...(delegatedKey
            ? {
                delegatedName,
                delegatedKey,
              }
            : {}),
        },
        signature,
      },
    });

    return {
      ...data,
    };
  }

  /** @internal */
  public async getNotices(): Promise<ApiNotices> {
    return this.get('/internal/notices');
  }

  /** @internal */
  // public async getUserSettings(): Promise<ApiUserSettings> {
  //   return this.get('/user/settings');
  // }

  /**
   * @see https://github.com/lukes/ISO-3166-Countries-with-Regional-Codes
   * @see https://github.com/lukes/ISO-3166-Countries-with-Regional-Codes/blob/master/slim-2/slim-2.json
   * @internal
   */
  // public async getUserCountries(): Promise<ApiCountry[]> {
  //   return this.get('/user/countries');
  // }

  /** @internal */
  // public async updateUserSettings(
  //   userId: APIUser['id'],
  //   settings: ApiUserSettings
  // ): Promise<{
  //   lastUpdatedAt: number;
  //   gdprConsent: number;
  //   favoriteMarkets?: string[];
  //   dismissedNotices?: string[];
  // }> {
  //   return this.patch('/user/settings', { userId, settings });
  // }

  /**
   * Confirm email or forgot password email - After retrieving the confirmation code from the user
   * @internal
   */
  public async userConfirm(
    body:
      | {
          type: 'confirmEmail';
          userId: string;
          code: string | number;
        }
      | {
          type: 'forgotPassword';
          userId: string;
          code: string | number;
          newPassword: string;
        }
  ): Promise<{ success: true }> {
    return this.post('/user/confirm', body);
  }

  /** @internal */
  public async userForgotPassword(
    email: string,
    captchaResponse: string
  ): Promise<{ success: true }> {
    return this.request('/user/forgot-password', {
      method: 'POST',
      data: { email },
      headers: { 'KP-Challenge-Token': captchaResponse },
    }) as Promise<any>;
  }

  // /** @internal */
  /**
   * NOTE: this is overlapping with sdk's user endpoint
   *
   * Default 24 hour withdrawal metrics for all accounts associated with
   * the given user.  can set { period: number } to change this or can also
   * set { metrics: ['withdrawalVolume', 'tradeVolume'] } for other supported
   * metrics if needed.
   *
   * returns { period: number, metrics: { withdrawalVolume: number, .... } }
   *
   * @internal
   */
  // public async getUserMetrics(): Promise<ApiUserMetrics> {
  //   return this.get('/user/metrics');
  // }

  /**
   * Get internal candles endpoint which includes time of previous interval
   * @internal
   */
  public async getInternalCandles(
    props: RestRequestGetCandles & { countBack?: number }
  ): Promise<ApiInternalCandles> {
    return this.get('/internal/candles', props);
  }

  /**
   * returns { apiKeys: [ ... ] } where keys are masked and provide the meta about
   * each key, last used, etc.
   *
   * @internal
   */
  public async getUserApiKeys(): Promise<ApiUserTokens> {
    return this.get('/user/auth/token');
  }

  /**
   * @internal
   */
  public async createUserApiKey(
    scopes: ApiUserNewToken['scopes']
  ): Promise<ApiUserNewToken> {
    return this.post('/user/auth/token', { scopes });
  }

  /**
   * Expects { id: number | number[] } defining associated key ids to remove
   *
   * @internal
   */
  public async deleteUserApiKey(
    apiKey: ApiUserBearerToken['apiKey']
  ): Promise<void> {
    return this.delete('/user/auth/token', { apiKey });
  }

  public async postInternalReferralsRedeemRequest(
    wallet: string,
    referralCode: string
  ): Promise<void> {
    return this.post('/internal/referrals/redeem', { wallet, referralCode });
  }

  /**
   * @internal
   */
  public async getInternalBalanceHistory(params: {
    wallet: string;
    page: number;
    limit: number;
  }): Promise<ApiBalanceHistoryRaw> {
    return this.get('/internal/balanceHistory', params);
  }

  /**
   * @deprecated
   * Enable staking enrolment for given wallet
   */
  public postEnrollTier3Staking(
    _wallet: string,
    _chain: StakingChains
  ): Promise<void> {
    throw new Error('Enrollment not available');
    // return this.post('/internal/tier3Staking', { wallet, chain });
  }

  public postStakingHostingOperatorSettings(props: {
    wallet: string;
    isEnabled: boolean;
    /**
     * Up to 1 decimal fee percent
     */
    hostingFeeInPercent: string;
  }): Promise<void> {
    return this.postWithHeaders(
      '/internal/staking/replicator/hostingOperatorSettings',
      props
    );
  }

  /**
   * @deprecated
   * Community staking wallet information
   * https://docs.google.com/document/d/1uPaR0jqisaDor7-dyUqW2VeakwZanJwgqXRXiNQwF94/edit#
   */
  public getInternalTier3Staking(_wallet: string): Promise<ApiStakingTier3> {
    throw new Error('Tier3 stats disabled');
    // return this.get('/internal/tier3Staking', { wallet });
  }

  // /**
  //  * Get onramps config so we can load the buy crypto widgets
  //  */
  // public getOnrampsConfig(wallet: string): Promise<OnrampConfigResponse> {
  //   return this.post('/internal/onramps', {
  //     wallet,
  //   });
  // }

  // /**
  //  * Get a service config on demand for service that does not return a `url` directly
  //  * in the config response.  This is called at the time the user requests the widget
  //  * of that type.
  //  */
  // public getOnrampsServiceConfig<S extends OnrampsValidGetServices>(
  //   wallet: string,
  //   service: S
  // ): Promise<OnrampServiceConfigResponse<S>> {
  //   return this.get('/internal/onramps', {
  //     wallet,
  //     service,
  //   });
  // }

  public getPayoutsAuthorizations(
    wallet: string,
    programCode: PayoutProgramCode
  ): Promise<ApiPayoutAuthorization[]> {
    return this.get('/payouts/authorizations', {
      wallet,
      program: programCode,
    });
  }

  private async internalAsDepositPayouts<T>(
    action: 'authorize' | 'fetch',
    wallet: string,
    programCode: PayoutAsDepositProgramCode,
    vaultManagerWallet?: string
  ): Promise<T> {
    if (programCode === 'syntheticFarms' && !vaultManagerWallet) {
      // RestRequestFarmPayout
      throw new Error('Missing VaultManager parameter for SyntheticFarms');
    }
    if (programCode === 'referralRewards' && vaultManagerWallet) {
      throw new Error('VaultManager parameter not supported');
    }
    //
    const pathCode: {
      [code in typeof programCode]: string;
    } = {
      referralRewards: 'referrals',
      syntheticFarms: 'farms',
    };
    const endpoint = `/internal/${pathCode[programCode]}/payouts`;
    const params = vaultManagerWallet
      ? ({
          wallet,
          program: programCode,
          managedAccount: vaultManagerWallet,
        } as RestRequestFarmPayout)
      : { wallet, program: programCode };

    if (action === 'authorize') {
      return this.post(endpoint, params);
    }
    return this.get(endpoint, params);
  }

  /** Starts payout process */
  public async authorizeAsDepositPayout(
    wallet: string,
    programCode: PayoutAsDepositProgramCode,
    vaultManagerWallet?: string
  ): Promise<{
    txHash: string;
    // RestRequestFarmPayout for syntheticFarms
  }> {
    return this.internalAsDepositPayouts(
      'authorize',
      wallet,
      programCode,
      vaultManagerWallet
    );
  }

  // TODO: "asDeposit" - better name ?
  public async fetchAsDepositPayouts(
    wallet: string,
    programCode: PayoutAsDepositProgramCode,
    vaultManagerWallet?: string
  ): Promise<ApiPayoutProgram & { payoutWalletAddress: string }> {
    return this.internalAsDepositPayouts(
      'fetch',
      wallet,
      programCode,
      vaultManagerWallet
    );
  }

  public updateInitialMarginFractionOverride({
    wallet,
    market,
    initialMarginFractionOverride,
  }: {
    wallet: string;
    market: string;
    initialMarginFractionOverride: number;
  }): Promise<ApiInitialMarginFractionOverride> {
    return this.post('/initialMarginFractionOverride', {
      nonce: IGNORED_NONCE,
      wallet,
      market,
      // Initial Margin Fraction override value between 1 and initialMarginFraction of the market; requests that do not include a initialMarginFractionOverride parameter remove any existing override for the wallet
      initialMarginFractionOverride,
    });
  }

  // public getPointsProgramSeasons(): Promise<APITradeRewardsProgramSprints> {
  //   return this.get('/internal/pointsProgram/seasons');
  // }

  // public getPoinstProgramSeasonStats(
  //   seasonId: string,
  //   wallet?: string
  // ): Promise<APITradeRewardsProgamStats> {
  //   return this.get('/internal/pointsProgram/season', wallet ? { wallet, seasonId } : { seasonId });
  // }

  // public enablePointsProgramStaking(wallet: string): Promise<void> {
  //   return this.post('/internal/pointsProgram/staking', { wallet });
  // }

  public updateReferralCode(
    wallet: string,
    newReferralCode: string
  ): Promise<void> {
    return this.post('/internal/referrals/codes', {
      wallet,
      code: newReferralCode,
    });
  }

  /**
   * Replicator staking (node) wallet/public information
   */
  public getInternalTier2Staking(wallet?: string): Promise<ApiStakingTier2> {
    return this.get(
      '/internal/staking/replicator',
      wallet ? { wallet } : undefined
    );
  }

  public putStakingHostingSubscription(
    wallet: string,
    /**
     * The name of the node which the wallet wishes to delegate to
     */
    nodeName: string
  ): Promise<ApiStakingTier2> {
    return this.putWithHeaders(
      '/internal/staking/replicator/hostingSubscription',
      { wallet, nodeName }
    );
  }

  public deleteStakingHostingSubscription(
    wallet: string
  ): Promise<ApiStakingTier2> {
    return this.deleteWithHeaders(
      '/internal/staking/replicator/hostingSubscription',
      { wallet }
    );
  }

  public getInternalTier2StakingStats(): Promise<ApiStakingTier2> {
    return this.get('/internal/staking/replicator/stats');
  }

  /**
   * Returns a (cached) list of nodes that are currently online
   * and that have hosting enabled (ie. a wallet can subscribe
   * (delegate) to this node)
   *
   * @note The list is sorted by uptime (highest first) and
   *       amount staked (highest first) (secondary sort)
   *
   * @see > {@link https://docs.google.com/document/d/13onBjWYx2Vqfd41LqakLr6IFPInb4gpwcpRhwBfQcVk/edit#heading=h.nscge7fzlhn3 Hosted Staking API Changes}
   */
  public getOnlineHostedStakingNodes(): Promise<ApiStakingHostedNode[]> {
    return this.get('/internal/staking/replicator/onlineHostedStakingNodes');
  }

  public getInternalReferralsStatus(
    wallet: string
  ): Promise<ApiReferralsStatus> {
    return this.get('/internal/referrals/status', { wallet });
  }

  public getTradingCompetitionRounds() {
    return this.get<ApiTradingCompetitionsResponse>(
      '/internal/tradingCompetitionV2/list'
    ).then(response => response.competitions);
  }

  public getTradingCompetitionRound({
    round,
    wallet,
  }: {
    round: string;
    wallet?: string;
  }) {
    return this.get<ApiTradingCompetitionResponse>(
      `/internal/tradingCompetitionV2/name/${round}`,
      wallet ? { wallet } : undefined
    );
  }

  public postEnrollToTradingCompetition({
    wallet,
    round,
  }: {
    wallet: string;
    round: string;
  }) {
    return this.post('/internal/tradingCompetitionV2/register', {
      wallet,
      name: round,
    });
  }

  public updateTradingCompetitionParticipant({
    wallet,
    roundName,
    userDisplayName,
  }: UpdateCompetitionParticipantParams): Promise<void> {
    return this.post(`/internal/tradingCompetitionV2/displayName`, {
      wallet,
      name: roundName,
      displayName: userDisplayName,
    });
  }

  /** @deprecated Sandbox competition */
  public getTradingCompetition(
    wallet?: string
  ): Promise<ApiTradingCompetitionSandbox> {
    return this.get(
      '/internal/tradingCompetition',
      wallet ? { wallet } : undefined
    );
  }

  /** @deprecated Sandbox competition */
  public updateTradingCompetitionParticipantOnSandbox({
    wallet,
    newName,
  }: UpdateCompetitionParticipantParamsSandbox): Promise<void> {
    return this.post('/internal/tradingCompetition/name', {
      wallet,
      name: newName,
    });
  }

  /** @deprecated Sandbox competition */
  public internalCompetitionRespawnParticipantWalletSandbox(wallet: string) {
    return this.post('/internal/tradingCompetition/respawn', {
      wallet,
    });
  }

  /** @internal */
  public async getInternalVaults(
    params: RestRequestGetVaults
  ): Promise<RestResponseGetVaults> {
    return this.get('/internal/vaults', params);
  }

  /** @internal */
  public async getInternalVault(
    params: RestRequestGetVaults
  ): Promise<KatanaPerpsVault> {
    const vaults = await this.get<RestResponseGetVaults>(
      '/internal/vaults',
      params
    );
    return vaults.vaults[0];
  }

  /** @internal Same as `getWallets()` but publicly available for Vaults */
  public async getInternalWalletsForVault(
    params: RestRequestGetWallets
  ): Promise<RestResponseGetWallets> {
    const vaults = await this.get<RestResponseGetWallets>(
      '/internal/vaults/wallets',
      params
    );
    return vaults;
  }

  /** @internal Same as `getHistoricalPnL()` but publicly available for Vaults */
  public async getInternalHistoricalPnLForVault(
    params: RestRequestGetHistoricalPnL
  ): Promise<RestResponseGetHistoricalPnL> {
    const vaults = await this.get<RestResponseGetHistoricalPnL>(
      '/internal/vaults/historicalPnL',
      params
    );
    return vaults;
  }

  /** @internal Same as `getHistoricalPnL()` but publicly available for Vaults */
  public async getInternalInitialMarginFractionOverrideForVault(
    params: RestRequestGetInitialMarginFractionOverride
  ): Promise<RestResponseGetInitialMarginFractionOverride> {
    const vaults = await this.get<RestResponseGetInitialMarginFractionOverride>(
      '/internal/vaults/initialMarginFractionOverride',
      params
    );
    return vaults;
  }

  /** @internal Same as `getFundingPayments()` but publicly available for Vaults */
  public async getInternalFundingPaymentsForVault(
    params: RestRequestGetFundingPayments
  ): Promise<Paginated<RestResponseGetFundingPayments>> {
    return this.get('/internal/vaults/fundingPayments', params);
  }

  /** @internal */
  public async getInternalVaultDepositors(
    params: RestRequestGetVaultDepositors
  ): Promise<RestResponseGetVaultDepositors> {
    return this.get('/internal/vaults/depositors', params);
  }

  /** @internal */
  public async getInternalVaultBalanceHistory(
    params: RestRequestGetVaultBalanceHistory & {
      page: number;
      limit: number;
    }
  ): Promise<RestResponseGetVaultBalanceHistory> {
    return this.get('/internal/vaults/balanceHistory', params);
  }

  /** @internal */
  public async getInternalVaultDepositorBalanceHistory(
    params: RestRequestGetVaultDepositorBalanceHistory & {
      page: number;
      limit: number;
    }
  ): Promise<RestResponseGetVaultDepositorBalanceHistory> {
    return this.get('/internal/vaults/depositorBalanceHistory', params);
  }

  protected async postWithHeaders(
    endpoint: string,
    data: Record<string, any> = {},
    headers?: { [key: string]: undefined | string }
  ) {
    const response = await this.request(endpoint, {
      method: 'POST',
      data,
      headers,
    });
    return response.data;
  }

  protected async putWithHeaders(
    endpoint: string,
    data: Record<string, any> = {},
    headers?: { [key: string]: undefined | string }
  ) {
    const response = await this.request(endpoint, {
      method: 'PUT',
      data,
      headers,
    });
    return response.data;
  }

  protected async deleteWithHeaders(
    endpoint: string,
    data: Record<string, any> = {},
    headers?: { [key: string]: undefined | string }
  ) {
    const response = await this.request(endpoint, {
      method: 'DELETE',
      data,
      // paramsSerializer,
      headers,
    });
    return response.data;
  }
}

const { katanaChain /* , ethereum, bsc */ } = config.chains;

const publicApiKatana = new RestPublicInternalClient({
  baseURL: katanaChain.restApiUrl,
  sandbox: config.envIsSandbox,
  axiosConfig,
});

const privateApiKatana = new RestAuthenticatedInternalClient({
  baseURL: katanaChain.restApiUrl,
  sandbox: config.envIsSandbox,
  chainId: katanaChain.chainId,
  exchangeContractAddress: katanaChain.exchangeContract,
  bridgeAdapterContractAddress:
    katanaChain.stargateAdapterV3Address || ETH_ADDRESS_ZERO,
});

// temporary

// const publicApiEthereum = new RestPublicInternalClient({
//   baseURL: ethereum.restApiUrl,
//   chain: 'eth',
// });

// export const privateApiEthereum = new RestAuthenticatedInternalClient({
//   baseURL: ethereum.restApiUrl,
//   chain: 'eth',
// });

// // Binance Smart Chain API
// const publicApiBsc = new RestPublicInternalClient({
//   baseURL: bsc.restApiUrl,
//   chain: 'bsc',
// });

// const privateApiBsc = new RestAuthenticatedInternalClient({
//   baseURL: bsc.restApiUrl,
//   chain: 'bsc',
// });

patchClientToIdexInternal(publicApiKatana as any);
patchClientToIdexInternal(privateApiKatana as any);

// patchClientToIdexInternal(publicApiEthereum as any);
// patchClientToIdexInternal(privateApiEthereum as any);

// patchClientToIdexInternal(publicApiBsc as any);
// patchClientToIdexInternal(privateApiBsc as any);

export const getChainPrivateApi = (chain?: Chains) => {
  const activeChain = chain || getActiveChainFromState();

  if (!activeChain) {
    throw new Error('Active chain not detected');
  }
  return activeChain === 'katanaEVMChain'
    ? privateApiKatana
    : // : activeChain === 'ethereumChain'
      // ? privateApiEthereum
      // : activeChain === 'binanceChain'
      // ? privateApiBsc
      (function () {
        throw new Error('Internal error: Missing chain API');
      })();
};

export const getChainPublicApi = (chain?: Chains) => {
  const activeChain = chain || getActiveChainFromState();
  if (!activeChain) {
    throw new Error('Active chain not detected');
  }
  if (activeChain !== 'katanaEVMChain') {
    throw new Error('Only Katana API supported');
  }
  return publicApiKatana;
  // return activeChain === 'binanceChain'
  //   ? publicApiBsc
  //   : activeChain === 'ethereumChain'
  //   ? publicApiEthereum
  //   : publicApiPolygon;
};

// Chain specific API has also shared/main endpoints
export const getMainPrivateApi = () => privateApiKatana;

// Not needed, Chain specific API will has also shared/main endpoints
// export const getMainPublicApi = () => getChainPublicApi();
